import { Linkedin } from 'lucide-react';
import useInView from '../hooks/useInView';

const TEAM = [
  {
    photo: '/team/anubhav.jpg',
    name: 'Anubhav Kumar',
    title: 'Executive Director',
    linkedin: 'https://www.linkedin.com/in/anubhavak/',
    bio: 'Entrepreneur with 15+ years of experience across India and the UK. IE Business School graduate. Leads strategy, client partnerships, and delivery excellence at IndUS Target.',
    tags: ['Strategy', 'Client Relations', 'UK Startup Experience'],
  },
  {
    photo: '/team/nicole.jpg',
    name: 'Nicole Fulfer',
    title: 'Head of US Talent',
    linkedin: 'https://www.linkedin.com/in/nicolefulfer/',
    bio: 'Ex-Amazon, Palo Alto Networks, and Microsoft. 13+ years in talent acquisition focused on tech and sales hiring. Leads our US recruitment practice and high-growth startup partnerships.',
    tags: ['Tech Hiring', 'Startup Scaling', 'Sales Talent'],
  },
  {
    photo: '/team/gaurav.jpg',
    name: 'Gaurav Shetty',
    title: 'Head of Operations',
    linkedin: 'https://www.linkedin.com/in/gaurav-shetty-4284a7166/',
    bio: 'Computer Science Engineer turned HR professional. 8 years in startup hiring strategy. Combines business acumen with recruitment expertise to drive operational excellence.',
    tags: ['Startup Strategy', 'Operations', 'Process Design'],
  },
];

export default function TeamSection() {
  const [ref, inView] = useInView();

  return (
    <section id="team" className="py-24 bg-white" data-testid="team-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="grid lg:grid-cols-2 items-end gap-8 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-6 h-px bg-[#E8500A]" />
              <span className="text-[#E8500A] text-xs font-semibold uppercase tracking-[0.22em]" style={{ fontFamily: '"Inter",sans-serif' }}>
                The Team
              </span>
            </div>
            <h2
              className="text-4xl sm:text-5xl font-black text-[#0A1628] leading-none tracking-tight"
              style={{ fontFamily: '"Satoshi","Inter",sans-serif' }}
              data-testid="team-heading"
            >
              Built by People Who<br />Have Been In Your Seat.
            </h2>
          </div>
          <p className="text-[#4B5563] text-base leading-relaxed lg:text-right" style={{ fontFamily: '"Inter",sans-serif' }}>
            Our team brings founder-level empathy, CXO-level access, and the operational rigor to deliver — every time.
          </p>
        </div>

        {/* Team cards */}
        <div ref={ref} className="grid md:grid-cols-3 gap-6">
          {TEAM.map((member, i) => (
            <div
              key={i}
              className="group border border-black/8 bg-white hover:border-[#0A1628]/20 hover:shadow-xl transition-all duration-300 overflow-hidden"
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(28px)',
                transition: `all 0.65s ease ${i * 150}ms`,
              }}
              data-testid={`team-card-${i}`}
            >
              {/* Photo */}
              <div className="relative h-56 overflow-hidden bg-[#0A1628]">
                <img
                  src={member.photo}
                  alt={member.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.querySelector('.photo-fallback').style.display = 'flex';
                  }}
                />
                {/* Fallback initials */}
                <div className="photo-fallback hidden w-full h-full items-center justify-center bg-[#0A1628]">
                  <span className="text-white text-4xl font-black" style={{ fontFamily: '"Satoshi","Inter",sans-serif' }}>
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                {/* Gradient overlay for text legibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/60 via-transparent to-transparent" />
              </div>

              {/* Content */}
              <div className="p-7">
                <h3
                  className="text-lg font-black text-[#0A1628] mb-0.5 leading-snug"
                  style={{ fontFamily: '"Satoshi","Inter",sans-serif' }}
                >
                  {member.name}
                </h3>
                <div className="text-xs text-[#E8500A] font-semibold uppercase tracking-[0.15em] mb-4" style={{ fontFamily: '"Inter",sans-serif' }}>
                  {member.title}
                </div>
                <p className="text-sm text-[#4B5563] leading-relaxed mb-5" style={{ fontFamily: '"Inter",sans-serif' }}>
                  {member.bio}
                </p>

                <div className="flex flex-wrap gap-1.5 mb-5">
                  {member.tags.map((tag, j) => (
                    <span
                      key={j}
                      className="text-xs bg-[#F9FAFB] border border-black/8 px-2.5 py-1 text-[#4B5563] rounded-sm"
                      style={{ fontFamily: '"Inter",sans-serif' }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <a
                  href={member.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-xs text-[#0A1628]/50 hover:text-[#0077B5] transition-colors duration-200 font-semibold"
                  style={{ fontFamily: '"Inter",sans-serif' }}
                  data-testid={`team-linkedin-${i}`}
                >
                  <Linkedin size={14} />
                  Connect on LinkedIn
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
